import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
  useToast
} from '@chakra-ui/react';
import { FaLock } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import SplashScreen from './SplashScreen';

const CriarSenha = ({ setUser }) => {
  const sharedInputStyles = {
    bg: 'white',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    color: '#0b1725',
    fontSize: { base: '16px', md: '18px' },
    letterSpacing: '0.01em',
    fontWeight: '600',
    borderRadius: '16px',
    boxShadow: '0 16px 35px rgba(10, 31, 75, 0.4)',
    px: 4,
    py: 4,
    transition: 'all 0.2s ease',
    _hover: { borderColor: '#ffffff' },
    _placeholder: {
      color: 'rgba(11, 23, 37, 0.65)',
      fontWeight: '500',
      fontSize: { base: '14px', md: '15px' },
      letterSpacing: '0.005em'
    },
    _focusVisible: {
      borderColor: '#ffffff',
      boxShadow: '0 0 0 3px rgba(255,255,255,0.35)'
    }
  };

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const pendingAccess = useMemo(() => {
    const raw = sessionStorage.getItem('pending_access');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!pendingAccess) {
      navigate('/login');
    }
  }, [pendingAccess, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = pendingAccess?.userId
        ? { userId: pendingAccess.userId, newPassword: password }
        : { cim: pendingAccess?.cim, cpf: pendingAccess?.cpf, newPassword: password };

      await axios.post(`${API_BASE}/auth/set-password`, payload);
      const loginResp = await axios.post(`${API_BASE}/auth/login`, {
        cim: pendingAccess?.cim,
        cpf: pendingAccess?.cpf,
        password
      });

      const userData = loginResp.data?.user || loginResp.data?.userData || loginResp.data?.result?.[0]?.[0];
      if (!userData) {
        throw new Error('Usuario nao retornado.');
      }

      sessionStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.removeItem('pending_access');
      setUser(userData);
      setShowSplash(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      const message = err.response?.data?.error || 'Erro ao salvar senha. Tente novamente.';
      setError(message);
      toast({
        title: 'Não foi possível salvar a senha',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <Box minH="100vh" position="relative" overflow="hidden">
      <Box
        position="absolute"
        inset="0"
        backgroundImage="url('/fundo.jpeg')"
        backgroundSize="cover"
        backgroundPosition="52% 18%"
      />
      <Box
        position="absolute"
        inset="0"
        bg="linear-gradient(180deg, rgba(20,50,100,0.38), rgba(10,26,36,0.62))"
      />

      <Flex
        position="relative"
        zIndex={1}
        minH="100vh"
        direction="column"
        align={{ base: 'stretch', md: 'center' }}
        justify={{ base: 'space-between', md: 'center' }}
        px={{ base: 4, sm: 6, md: 10 }}
        py={{ base: 8, md: 10 }}
      >
        <VStack spacing={2} align="center" mt={{ base: 4, md: 8 }}>
          <Text
            fontSize={{ base: '24px', md: '28px' }}
            fontFamily="Marcellus"
            textTransform="uppercase"
            letterSpacing="0.18em"
            color="white"
            textShadow="0 3px 14px rgba(0,0,0,0.65)"
          >
            Primeiro acesso
          </Text>
          <Text color="whiteAlpha.800" textAlign="center" fontSize={{ base: 'sm', md: 'md' }}>
            Crie sua senha para continuar no aplicativo.
          </Text>
        </VStack>

        <Box width="100%" maxW={{ base: '100%', sm: '460px', md: '480px' }} mt={{ base: 6, md: 10 }}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaLock color="#1F4B99" size="18" />
                  </InputLeftElement>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Nova senha"
                    {...sharedInputStyles}
                  />
                </InputGroup>
              </FormControl>

              <FormControl>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaLock color="#1F4B99" size="18" />
                  </InputLeftElement>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmar nova senha"
                    {...sharedInputStyles}
                  />
                </InputGroup>
              </FormControl>

              {error && (
                <Text color="red.300" fontSize="sm" textAlign="center">
                  {error}
                </Text>
              )}

              <Button
                type="submit"
                bg="#1F4B99"
                color="white"
                size="lg"
                width="full"
                borderRadius="18px"
                _hover={{ bg: '#163c7a' }}
                isLoading={isLoading}
                loadingText="Salvando..."
              >
                Salvar senha
              </Button>
            </VStack>
          </form>
        </Box>
      </Flex>
    </Box>
  );
};

export default CriarSenha;
